import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import { stageFor } from './utils/competitionStage';
import notify from './utils/notify';
import './TournamentManager.css';
import { useNavigate, useLocation } from 'react-router-dom';
import './Home.css';
import { FiBarChart2, FiSettings, FiGrid, FiEdit, FiTrash2, FiAward, FiChevronRight, FiPlus } from 'react-icons/fi';
import { LuTable } from 'react-icons/lu';
import ConfirmationModal from './components/ConfirmationModal';
import DangerConfirmModal from './components/DangerConfirmModal';
import { deleteSummaryItems } from './utils/deleteSummary';
import BottomNav from './BottomNav';
import { useApi } from './useApi';
import { useAuth } from './AuthContext';
import { Permission } from './permissions';
import PageHeader from './components/PageHeader';
import { useQuery, useQueryClient } from 'react-query';

const SHORT_MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const SHORT_DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const INITIAL_UPCOMING = 3;
// Completed competitions are reachable only through search, so this list only
// has to be deep enough to hold everything still in play.
const DEFAULT_LIMIT = 20;

// Tournament.date is a free-form nullable string column, so nothing downstream
// may assume it parses. Anything that isn't a clean YYYY-MM-DD is treated as
// undated: it still lists under Recent rather than silently disappearing.
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const isDated = (t) => ISO_DATE.test(t?.date || '');

const formatDateParts = (dateStr) => {
  if (!ISO_DATE.test(dateStr || '')) return { day: '', month: '', weekday: '' };
  const [year, monthNum, dayNum] = dateStr.split('-').map(Number);
  const weekday = SHORT_DAYS[new Date(year, monthNum - 1, dayNum).getDay()] || '';
  return { day: dayNum, month: SHORT_MONTHS[monthNum - 1] || '', weekday };
};

// Whole-day difference, computed on local midnights so it can't drift by an hour.
const daysUntil = (dateStr) => {
  if (!ISO_DATE.test(dateStr || '')) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  const then = new Date(y, m - 1, d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((then - today) / 86400000);
};

const relativeLabel = (dateStr) => {
  const diff = daysUntil(dateStr);
  if (diff === null) return '';
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff < 0) return '';
  if (diff < 14) return `in ${diff} days`;
  if (diff < 60) return `in ${Math.round(diff / 7)} weeks`;
  return '';
};

const titleCase = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

const metaLineFor = (tournament) => {
  const parts = [];
  if (tournament.format) parts.push(titleCase(tournament.format));
  if (tournament.signup_mode === 'roll_up') parts.push('Roll up');
  if (tournament.location) parts.push(tournament.location);
  return parts.join(' · ');
};

/**
 * Swipe-to-reveal edit/delete shell. Lifted out of the old card unchanged so
 * every card variant on the page inherits the same gesture.
 */
const SwipeShell = memo(({ tournament, userProfile, hasPermission, onEdit, onDelete, openSwipeId, setOpenSwipeId, onActivate, className, children }) => {
  const cardRef = useRef(null);
  const touchRef = useRef({ startX: 0, startY: 0, currentX: 0, isDragging: false, isScrolling: false });

  const canEdit = hasPermission(Permission.EDIT_TOURNAMENT) || userProfile?.id === tournament.creator_id;
  const canDelete = hasPermission(Permission.DELETE_TOURNAMENT) || userProfile?.id === tournament.creator_id;
  const hasActions = canEdit || canDelete;
  const actionsWidth = (canEdit ? 70 : 0) + (canDelete ? 70 : 0);
  const isOpen = openSwipeId === tournament.id;

  useEffect(() => {
    if (openSwipeId !== tournament.id && cardRef.current) {
      cardRef.current.style.transition = 'transform 0.3s ease';
      cardRef.current.style.transform = 'translateX(0)';
    }
  }, [openSwipeId, tournament.id]);

  const onTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    touchRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      currentX: isOpen ? -actionsWidth : 0,
      isDragging: false,
      isScrolling: false,
    };
    if (cardRef.current) {
      cardRef.current.style.transition = 'none';
    }
  }, [isOpen, actionsWidth]);

  const onTouchMove = useCallback((e) => {
    const touch = e.touches[0];
    const deltaX = touch.clientX - touchRef.current.startX;
    const deltaY = touch.clientY - touchRef.current.startY;

    if (!touchRef.current.isDragging && !touchRef.current.isScrolling) {
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        touchRef.current.isScrolling = true;
        return;
      }
      if (Math.abs(deltaX) > 10) {
        touchRef.current.isDragging = true;
      }
    }

    if (touchRef.current.isScrolling || !touchRef.current.isDragging) return;

    let translateX = isOpen ? deltaX - actionsWidth : deltaX;
    translateX = Math.min(0, Math.max(-actionsWidth, translateX));
    touchRef.current.currentX = translateX;
    if (cardRef.current) {
      cardRef.current.style.transform = `translateX(${translateX}px)`;
    }
  }, [isOpen, actionsWidth]);

  const onTouchEnd = useCallback(() => {
    if (cardRef.current) {
      cardRef.current.style.transition = 'transform 0.3s ease';
    }

    if (!touchRef.current.isDragging) return;

    const threshold = actionsWidth / 2;
    if (Math.abs(touchRef.current.currentX) > threshold) {
      if (cardRef.current) {
        cardRef.current.style.transform = `translateX(-${actionsWidth}px)`;
      }
      setOpenSwipeId(tournament.id);
    } else {
      if (cardRef.current) {
        cardRef.current.style.transform = 'translateX(0)';
      }
      if (isOpen) setOpenSwipeId(null);
    }
  }, [actionsWidth, isOpen, setOpenSwipeId, tournament.id]);

  const handleCardClick = useCallback(() => {
    if (touchRef.current.isDragging) return;
    if (isOpen) {
      setOpenSwipeId(null);
      return;
    }
    onActivate(tournament);
  }, [isOpen, setOpenSwipeId, onActivate, tournament]);

  return (
    <div className="swipe-container">
      {hasActions && (
        <div className="swipe-actions" style={{ width: actionsWidth }}>
          {canEdit && (
            <button className="swipe-action-btn swipe-edit" onClick={() => onEdit(tournament)}>
              <FiEdit size={20} />
              <span>Edit</span>
            </button>
          )}
          {canDelete && (
            <button className="swipe-action-btn swipe-delete" onClick={() => onDelete(tournament)}>
              <FiTrash2 size={20} />
              <span>Delete</span>
            </button>
          )}
        </div>
      )}
      <div
        className={className}
        ref={cardRef}
        onClick={handleCardClick}
        onTouchStart={hasActions ? onTouchStart : undefined}
        onTouchMove={hasActions ? onTouchMove : undefined}
        onTouchEnd={hasActions ? onTouchEnd : undefined}
      >
        {children}
      </div>
    </div>
  );
});

/**
 * One competition, one row. Every variant is the same two-line shape — date
 * block, name, meta — so a long list stays scannable and short. Live rows add
 * a leaderboard button; finished rows recede.
 *
 * Tapping opens setup for anything still editable, and the leaderboard for
 * anything finished. Swipe still reveals edit/delete on all of them.
 */
const CompRow = memo(({ tournament, variant, onLeaderboard, onStart, ...shell }) => {
  const { day, month } = formatDateParts(tournament.date);
  const stage = variant === 'done' ? null : stageFor(tournament);
  // Ready to go: the row opens the Rounds page to start it, not the wizard.
  const activate = stage?.ready && onStart ? onStart : shell.onActivate;
  const isLive = variant === 'live';
  const relative = variant === 'planned' ? relativeLabel(tournament.date) : '';

  // Which round it's on beats everything while playing; how soon it is beats
  // whether you're signed up while it's still being set up.
  let tag = '';
  let tagClass = '';
  if (isLive && tournament.latest_round > 1) {
    tag = `R${tournament.latest_round}`;
    tagClass = ' comp-row-tag--round';
  } else if (relative) {
    tag = relative;
  } else if (variant === 'planned' && tournament.is_user_signed_up) {
    tag = "You're in";
    tagClass = ' comp-row-tag--signed';
  }

  return (
    <SwipeShell tournament={tournament} className={`comp-row comp-row--${variant}`} {...shell} onActivate={activate}>
      <div className="comp-row-date" aria-hidden={!day}>
        {day ? <><span className="comp-row-day">{day}</span><span className="comp-row-mon">{month}</span></>
             : <span className="comp-row-nodate">—</span>}
      </div>

      <div className="comp-row-body">
        <div className="comp-row-title">
          {isLive && <span className="comp-row-dot" aria-hidden="true" />}
          <span className="comp-row-name">{tournament.name}</span>
        </div>
        <div className="comp-row-meta">
          <span className="comp-row-meta-text">{metaLineFor(tournament)}</span>
          {/* One tag only: a second one squeezes the meta line into an ellipsis
              on a 360px screen, and the format/venue matters more than either. */}
          {tag && <span className={`comp-row-tag${tagClass}`}>{tag}</span>}
        </div>
        {stage && (
          <div className={`comp-row-stage${stage.ready ? ' comp-row-stage--ready' : stage.todo ? ' comp-row-stage--todo' : ''}`}>
            {stage.text}
          </div>
        )}
      </div>

      {isLive ? (
        <button
          className="comp-row-action"
          onClick={(e) => { e.stopPropagation(); onLeaderboard(tournament); }}
          aria-label={`View leaderboard for ${tournament.name}`}
          title="Leaderboard"
        >
          <LuTable size={17} />
        </button>
      ) : (
        <FiChevronRight className="comp-row-chevron" size={17} />
      )}
    </SwipeShell>
  );
});

function TournamentManager() {
  const navigate = useNavigate();
  const location = useLocation();
  const mineOnly = new URLSearchParams(location.search).get('mine') === 'true';
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [openSwipeId, setOpenSwipeId] = useState(null);
  const limit = DEFAULT_LIMIT;
  const [showAllUpcoming, setShowAllUpcoming] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isForceConfirmOpen, setIsForceConfirmOpen] = useState(false);
  const [tournamentToDelete, setTournamentToDelete] = useState(null);
  // What the server says this delete would destroy, counted (see
  // `_tournament_delete_summary`). Only the force branch has one: the first
  // ask is for a day with nothing on it to lose.
  const [deleteSummary, setDeleteSummary] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const { authenticatedFetch } = useApi();
  const { currentSociety, userProfile, hasPermission, refreshPlayerStatus } = useAuth();
  const canCreate = hasPermission(Permission.CREATE_TOURNAMENT);

  const menuItems = useMemo(() => [
    { name: 'Leaderboard', route: '/leaderboard', icon: <FiBarChart2 size={24} /> },
    { name: 'Rounds', route: '/tournaments/scorecard-entry', icon: <FiGrid size={24} /> },
    { name: 'Competitions', route: '/tournaments', icon: <FiAward size={24} />, active: true },
    { name: 'Manage', route: '/setup', icon: <FiSettings size={24} /> },
  ], []);

  const { data: tournaments = [], isLoading } = useQuery(
    ['tournaments', currentSociety?.id, debouncedSearchTerm, mineOnly, limit],
    async () => {
      if (!currentSociety) return [];
      const params = new URLSearchParams({ q: debouncedSearchTerm, limit });
      if (mineOnly) params.set('mine', 'true');
      const res = await authenticatedFetch(`/tournaments?${params}`);
      if (res.ok) return res.json();
      return [];
    },
    { enabled: !!currentSociety, keepPreviousData: true }
  );

  // Grouped by lifecycle, not by date: this page exists to get you back into a
  // competition whose setup you can still change. Date grouping hid a
  // not-started competition among finished ones the moment its date slipped by.
  const { live, planned, done } = useMemo(() => {
    const liveList = [];
    const plannedList = [];
    const doneList = [];

    tournaments.forEach((t) => {
      if (t.status === 'in_progress') liveList.push(t);
      else if (t.status === 'complete') doneList.push(t);
      else plannedList.push(t);
    });

    // Soonest first, undated last — an undated draft is still work in progress.
    plannedList.sort((a, b) => {
      if (isDated(a) !== isDated(b)) return isDated(a) ? -1 : 1;
      return (a.date || '').localeCompare(b.date || '');
    });
    doneList.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    return { live: liveList, planned: plannedList, done: doneList };
  }, [tournaments]);

  const isSearching = !!debouncedSearchTerm;
  const visiblePlanned = showAllUpcoming ? planned : planned.slice(0, INITIAL_UPCOMING);

  const handleEditClick = useCallback((tournament) => {
    // Advanced competition days edit through their own wizard — the standard
    // one is built around scoring formats and player groups they don't use.
    if (tournament.is_advanced) {
      navigate('/tournaments/add-advanced', { state: { tournament } });
    } else {
      navigate('/tournaments/add', { state: { tournament } });
    }
  }, [navigate]);

  const handleStartClick = useCallback((tournament) => {
    navigate(`/tournaments/scorecard-entry?tournament=${tournament.id}`, { state: { viewMode: 'players' } });
  }, [navigate]);

  const handleLeaderboardClick = useCallback((tournament) => {
    navigate(`/leaderboard?tournament=${tournament.id}&live=true`);
  }, [navigate]);

  const handleDeleteClick = useCallback((tournament) => {
    setTournamentToDelete(tournament);
    setDeleteSummary(null);
    setDeleteError('');
    setIsConfirmOpen(true);
  }, []);

  const closeDeleteFlow = useCallback(() => {
    setIsConfirmOpen(false);
    setIsForceConfirmOpen(false);
    setTournamentToDelete(null);
    setDeleteSummary(null);
    setDeleteError('');
    setIsDeleting(false);
  }, []);

  const confirmDelete = async () => {
    // A swipe-to-delete row on a phone is easy to tap twice. The second call
    // would 404 and pop an alert over a delete that actually worked.
    if (!tournamentToDelete || isDeleting) return;
    setIsDeleting(true);

    try {
      const response = await authenticatedFetch(`/tournaments/${tournamentToDelete.id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        queryClient.invalidateQueries(['tournaments', currentSociety?.id]);
        refreshPlayerStatus();
        closeDeleteFlow();
      } else {
        const errorData = await response.json();

        if (response.status === 400 && errorData.requires_force) {
            // There IS something to lose. Hand the counts to the second ask,
            // which is a type-to-confirm rather than one more click.
            setDeleteSummary(errorData.summary || null);
            setIsConfirmOpen(false);
            setIsForceConfirmOpen(true);
            setIsDeleting(false);
        } else {
            notify.error(errorData.error || 'Failed to delete tournament');
            closeDeleteFlow();
        }
      }
    } catch (error) {
      console.error('Error deleting tournament:', error);
      notify.error('An unexpected error occurred while deleting the tournament.');
      closeDeleteFlow();
    }
  };

  const performForceDelete = async () => {
    if (!tournamentToDelete || isDeleting) return;
    setIsDeleting(true);
    setDeleteError('');

    try {
        const response = await authenticatedFetch(`/tournaments/${tournamentToDelete.id}?force=true`, {
            method: 'DELETE',
        });

        if (response.ok) {
            queryClient.invalidateQueries(['tournaments', currentSociety?.id]);
            refreshPlayerStatus();
            closeDeleteFlow();
        } else {
             const retryError = await response.json().catch(() => ({}));
             // Reported IN the modal, not in an alert that closes it: a failure
             // here leaves the day intact, and the organiser needs to see that
             // beside the thing they were deleting.
             setDeleteError(retryError.error || 'Failed to delete this competition.');
             setIsDeleting(false);
        }
    } catch (error) {
        console.error('Error force deleting tournament:', error);
        setDeleteError('An unexpected error occurred. The competition has not been deleted.');
        setIsDeleting(false);
    }
  };

  const shellProps = {
    userProfile,
    hasPermission,
    onEdit: handleEditClick,
    onDelete: handleDeleteClick,
    onActivate: handleEditClick,
    openSwipeId,
    setOpenSwipeId,
  };

  // A finished competition has no setup left to change, so tapping it shows the
  // result instead of reopening the wizard. Swipe still offers edit/delete.
  const doneShellProps = { ...shellProps, onActivate: handleLeaderboardClick };

  if (!currentSociety) {
    return (
      <div className="tournament-manager-page">
        <PageHeader title="Competitions" backTo="/setup" backLabel="Back to Manage" />
        <div className="no-society-view" style={{ padding: '40px', textAlign: 'center' }}>
            <h3>No Society Selected</h3>
            <p>Please select or create a society to manage tournaments.</p>
            <button onClick={() => navigate('/setup')} className="btn-primary-filled" style={{ marginTop: '20px' }}>Go to Setup</button>
        </div>
        <BottomNav menuItems={menuItems} />
      </div>
    );
  }

  const nothingAtAll = tournaments.length === 0 && !isLoading && !isSearching;

  return (
    <div className="tournament-manager-page">
      <ConfirmationModal
        tone="danger"
        isOpen={isConfirmOpen}
        title="Delete Tournament?"
        message={`Are you sure you want to delete ${tournamentToDelete?.name}?`}
        confirmText={isDeleting ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={closeDeleteFlow}
      />

      {/* The day has scores on it. Same bar as shutting down a society or
          deleting an account — the counts, then the word typed — because the
          consequence is the same order: every player's card for that day, gone,
          with no undo. The first ask above stays a single click; a day with
          nothing on it has nothing to lose. */}
      <DangerConfirmModal
        isOpen={isForceConfirmOpen}
        title="Delete this competition?"
        subject={tournamentToDelete?.name}
        items={deleteSummaryItems(deleteSummary)}
        body="Scores have already been recorded for this competition."
        confirmLabel="Delete competition"
        errorMessage={deleteError}
        isSubmitting={isDeleting}
        onConfirm={performForceDelete}
        onCancel={closeDeleteFlow}
      />

      <PageHeader
        title={mineOnly ? 'My Competitions' : 'Competitions'}
        backTo="/setup"
        backLabel="Back to Manage"
      />

      <div className="tournament-manager-content-wrapper">

        {canCreate && !nothingAtAll && (
          <button className="btn-new" onClick={() => navigate('/tournaments/add')}>
            <FiPlus size={18} /> New Competition
          </button>
        )}

        {/* First run: the hero becomes the whole page */}
        {nothingAtAll && (
          <div className="comp-empty">
            <div className="comp-empty-ring"><FiAward size={26} /></div>
            <h2 className="comp-empty-title">
              {mineOnly ? 'Nothing yet' : 'No competitions yet'}
            </h2>
            <p className="comp-empty-sub">
              {mineOnly
                ? "You haven't played in or signed up for any competitions yet."
                : canCreate
                  ? 'Set up your first competition — pick a date, a course and a format, then invite your players.'
                  : 'Nothing is scheduled. Your society admin sets these up.'}
            </p>
            {canCreate && !mineOnly && (
              <button className="comp-empty-cta" onClick={() => navigate('/tournaments/add')}>
                New Competition
              </button>
            )}
          </div>
        )}

        {/* Search results take over from the two working sections */}
        {isSearching ? (
          <section className="comp-section">
            <div className="comp-section-label">Results</div>
            {tournaments.length === 0 && !isLoading ? (
              <p className="comp-none">No competitions match &ldquo;{debouncedSearchTerm}&rdquo;.</p>
            ) : (
              <div className="comp-list">
                {tournaments.map((t) => {
                  const done = t.status === 'complete';
                  return (
                    <CompRow
                      key={t.id}
                      tournament={t}
                      variant={done ? 'done' : t.status === 'in_progress' ? 'live' : 'planned'}
                      onLeaderboard={handleLeaderboardClick}
                      onStart={handleStartClick}
                      {...(done ? doneShellProps : shellProps)}
                    />
                  );
                })}
              </div>
            )}
          </section>
        ) : (
          <>
            {/* 3. Being played right now */}
            {live.length > 0 && (
              <section className="comp-section">
                <div className="comp-section-label comp-section-label--live">
                  <span className="comp-label-dot" aria-hidden="true" />
                  In progress
                  <span className="comp-section-count">{live.length}</span>
                </div>
                <div className="comp-list">
                  {live.map((t) => (
                    <CompRow
                      key={t.id}
                      tournament={t}
                      variant="live"
                      onLeaderboard={handleLeaderboardClick}
                      onStart={handleStartClick}
                      {...shellProps}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* 4. Set up but not started — the page's main job */}
            {planned.length > 0 && (
              <section className="comp-section">
                <div className="comp-section-label">
                  Not started
                  <span className="comp-section-count">{planned.length}</span>
                </div>
                <div className="comp-list">
                  {visiblePlanned.map((t) => (
                    <CompRow key={t.id} tournament={t} variant="planned" {...shellProps} onStart={handleStartClick} />
                  ))}
                </div>
                {planned.length > INITIAL_UPCOMING && !showAllUpcoming && (
                  <button className="comp-more" onClick={() => setShowAllUpcoming(true)}>
                    Show {planned.length - INITIAL_UPCOMING} more
                  </button>
                )}
              </section>
            )}

            {/* Finished competitions are deliberately not listed — they are
                history, and the search below finds them by name. */}
            {!isLoading && live.length === 0 && planned.length === 0 && done.length > 0 && (
              <p className="comp-none">
                Nothing in progress or waiting to start.<br />
                Search below to find a completed competition.
              </p>
            )}

          </>
        )}

        {/* Search sits at the foot of the page: the working list is what you
            came for, and searching is the fallback when it isn't there. */}
        {!nothingAtAll && (
          <div className="comp-search">
            <svg className="search-icon-subtle" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
              <path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z"/>
            </svg>
            <input
              type="text"
              className="comp-search-field"
              placeholder="Search all competitions, including completed"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button className="comp-search-clear" onClick={() => setSearchTerm('')} aria-label="Clear search">
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      <BottomNav menuItems={menuItems} />
    </div>
  );
}

export default TournamentManager;
